import ExcelJS from "exceljs";
import Decimal from "decimal.js";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import { CellFormat } from "@/lib/excel/common";
import { safeParseDecimal, Fills } from "@/lib/excel";
import { ExcelComposer } from "@/lib/excel/composer";
import type { CellValue as ComposerCellValue, SectionConfig } from "@/lib/excel/composer";
import type { ReportCropProfileRow } from "@/lib/api/risk/profiles";
import { Commodities } from "@lhc/commodities/commodities";

// -----------------------------------------------------------------------
// Column layout for the source `Producer Risk` tab.
//
// The spec pins these by column LETTER rather than by header lookup
// because the source export's header text can drift between releases.
// -----------------------------------------------------------------------
export const SOURCE_COMMODITY_COL = "J";
export const SOURCE_CUSTOMER_COL = "N";
export const SOURCE_DELIVERY_END_DATE_COL = "Q";
export const SOURCE_OPEN_QTY_COL = "T";
export const SOURCE_GAIN_LOSS_COL = "AG";

const HEADER_ROW = 1;
const DATA_START_ROW = 2;

// -----------------------------------------------------------------------
// Bucket the Producer Risk display commodity into the four families that
// drive both the OC/NC cutoff rule and the acreage lookup.
//
// Anything outside this set gets routed to the unknown-commodity error
// bucket (per product decision; do NOT hard-error).
// -----------------------------------------------------------------------
export type CommodityBucket = "wheat" | "corn" | "soybeans" | "canola";

const COMMODITY_BUCKET_MAP: Record<string, CommodityBucket> = {
  "corn": "corn",
  "soybeans": "soybeans",
  "canola": "canola",
  // Both wheat variants in the Producer Risk export collapse to the
  // single "wheat" bucket. Acreage lookup pulls every `MW` commodity_type
  // profile for the customer regardless of which wheat variety the row
  // mentions (per product: there is no Winter Wheat profile, all wheat
  // acreage lives under MW).
  "winter wheat": "wheat",
  "minneapolis wheat": "wheat",
};

export function classifyCommodity(display: string | undefined | null): CommodityBucket | null {
  if (!display) return null;
  const key = display.toString().trim().toLowerCase();
  return COMMODITY_BUCKET_MAP[key] ?? null;
}

// -----------------------------------------------------------------------
// Delivery end date parsing.
//
// The spec calls out two shapes in column I:
//   - Literal "Delinquent" (case-insensitive) → always OC
//   - "MMM-YY" e.g. "Jan-27", "Mar-27" → compare against the cutoff
//
// Excel sometimes stores month-year strings as raw date serials. We try
// the cell's displayed/formatted text first; if that fails to parse as a
// MMM-YY we fall back to the underlying value as a Date.
// -----------------------------------------------------------------------
export type ParsedDeliveryEndDate =
  | { kind: "delinquent" }
  | { kind: "date"; date: moment.Moment }
  | { kind: "invalid"; raw: string };

export function parseDeliveryEndDate(rawValue: ExcelJS.CellValue, rawText: string): ParsedDeliveryEndDate {
  const text = (rawText || "").toString().trim();

  if (text && /^delinquent$/i.test(text)) {
    return { kind: "delinquent" };
  }

  if (text) {
    const parsed = moment.tz(text, "MMM-YY", true, CommonDestinationTimezone);
    if (parsed.isValid()) {
      return { kind: "date", date: parsed.startOf("month") };
    }
  }

  if (rawValue instanceof Date) {
    const parsed = moment.tz(rawValue, CommonDestinationTimezone);
    if (parsed.isValid()) {
      return { kind: "date", date: parsed.startOf("month") };
    }
  }

  return { kind: "invalid", raw: text || (rawValue == null ? "" : String(rawValue)) };
}

// -----------------------------------------------------------------------
// OC vs NC classification.
//
// Cutoffs (per the spec):
//   wheat   / canola   → Aug 1 of ncYear
//   corn               → Oct 1 of ncYear
//   soybeans           → Sep 1 of ncYear
//
// "Before cutoff" → OC. "On or after cutoff" → NC. Delinquent always OC.
// -----------------------------------------------------------------------
export type OcNcBucket = "OC" | "NC";

function commodityCutoff(bucket: CommodityBucket, ncYear: number): moment.Moment {
  switch (bucket) {
    case "wheat":
    case "canola":
      return moment.tz({ year: ncYear, month: 7, day: 1 }, CommonDestinationTimezone); // Aug 1
    case "corn":
      return moment.tz({ year: ncYear, month: 9, day: 1 }, CommonDestinationTimezone); // Oct 1
    case "soybeans":
      return moment.tz({ year: ncYear, month: 8, day: 1 }, CommonDestinationTimezone); // Sep 1
  }
}

export function classifyOcNc(params: {
  bucket: CommodityBucket;
  endDate: ParsedDeliveryEndDate;
  ncYear: number;
}): OcNcBucket | "invalid-date" {
  if (params.endDate.kind === "delinquent") return "OC";
  if (params.endDate.kind === "invalid") return "invalid-date";

  const cutoff = commodityCutoff(params.bucket, params.ncYear);
  return params.endDate.date.isBefore(cutoff) ? "OC" : "NC";
}

// -----------------------------------------------------------------------
// Acreage lookup.
//
// Per product:
//   - Match customer by exact `Customer.long_name`.
//   - For wheat rows, ANY profile whose `commodity_type === "MW"` counts
//     (there is no separate Winter Wheat profile).
//   - For corn / canola / soybeans, match the corresponding farm-base
//     commodity_type symbol.
//   - Missing profiles contribute 0 (auditable via the Acres tab).
// -----------------------------------------------------------------------
const COMMODITY_TYPE_FOR_BUCKET: Record<CommodityBucket, string> = {
  corn: "C",
  soybeans: "S",
  canola: "CNL",
  wheat: "MW",
};

interface ProfileIndex {
  // long_name → crop_year → commodity_type → profiles
  byCustomerYearType: Map<string, Map<number, Map<string, ReportCropProfileRow[]>>>;
}

function indexProfiles(profiles: ReportCropProfileRow[]): ProfileIndex {
  const byCustomerYearType = new Map<string, Map<number, Map<string, ReportCropProfileRow[]>>>();
  for (const profile of profiles) {
    const customerKey = normalizeCustomer(profile.Customer.long_name);
    let byYear = byCustomerYearType.get(customerKey);
    if (!byYear) {
      byYear = new Map();
      byCustomerYearType.set(customerKey, byYear);
    }
    let byType = byYear.get(profile.crop_year);
    if (!byType) {
      byType = new Map();
      byYear.set(profile.crop_year, byType);
    }
    const list = byType.get(profile.commodity_type) ?? [];
    list.push(profile);
    byType.set(profile.commodity_type, list);
  }
  return { byCustomerYearType };
}

function normalizeCustomer(name: string): string {
  return (name || "").trim().toLowerCase();
}

function lookupProfilesFor(params: {
  index: ProfileIndex;
  customerLongName: string;
  cropYear: number;
  bucket: CommodityBucket;
}): ReportCropProfileRow[] {
  const customerKey = normalizeCustomer(params.customerLongName);
  const commodityType = COMMODITY_TYPE_FOR_BUCKET[params.bucket];
  const byYear = params.index.byCustomerYearType.get(customerKey);
  if (!byYear) return [];
  const byType = byYear.get(params.cropYear);
  if (!byType) return [];
  return byType.get(commodityType) ?? [];
}

// Pull every profile (across every commodity_type) the customer has
// for the given crop year. The summary tabs use this so we credit a
// customer with every acre on file for that year, not just the acres
// belonging to commodities that happened to appear in the source rows.
function lookupAllProfilesForCustomerYear(params: {
  index: ProfileIndex;
  customerLongName: string;
  cropYear: number;
}): ReportCropProfileRow[] {
  const customerKey = normalizeCustomer(params.customerLongName);
  const byYear = params.index.byCustomerYearType.get(customerKey);
  if (!byYear) return [];
  const byType = byYear.get(params.cropYear);
  if (!byType) return [];
  const out: ReportCropProfileRow[] = [];
  for (const profiles of byType.values()) {
    out.push(...profiles);
  }
  return out;
}

// -----------------------------------------------------------------------
// Row-level data we capture for each kept (OC or NC) row.
// -----------------------------------------------------------------------
interface KeptRow {
  sourceRowNumber: number;
  customerLongName: string;
  bucket: CommodityBucket;
  openQty: Decimal;
  gainLossTotal: Decimal;
}

// -----------------------------------------------------------------------
// Errors / warnings surfaced back to the user.
// -----------------------------------------------------------------------
export interface ProcessingErrors {
  unknownCommodities: Array<{ row: number; commodity: string; customer: string }>;
  invalidDates: Array<{ row: number; commodity: string; customer: string; raw: string }>;
  unknownCustomers: string[];
}

// -----------------------------------------------------------------------
// Final result.
// -----------------------------------------------------------------------
export interface ProcessResult {
  buffer: Buffer;
  errors: ProcessingErrors;
  counts: {
    totalDataRows: number;
    ncRows: number;
    ocRows: number;
    skippedUnknownCommodity: number;
    skippedInvalidDate: number;
  };
  ncYear: number;
  ocYear: number;
}

// -----------------------------------------------------------------------
// Tab-name helpers
// -----------------------------------------------------------------------
const PRODUCER_RISK_TAB_REGEX = /producer\s*risk/i;

export function findProducerRiskTabs(workbook: ExcelJS.Workbook): ExcelJS.Worksheet[] {
  const matches: ExcelJS.Worksheet[] = [];
  workbook.eachSheet((sheet: ExcelJS.Worksheet) => {
    if (PRODUCER_RISK_TAB_REGEX.test(sheet.name)) {
      matches.push(sheet);
    }
  });
  return matches;
}

// -----------------------------------------------------------------------
// Cell read helpers.
// -----------------------------------------------------------------------
function cellText(cell: ExcelJS.Cell): string {
  // ExcelJS exposes the displayed string on `.text`. For formula cells
  // .text returns the calculated result. Falling back to raw `.value`
  // covers cases where `.text` is empty but value is a primitive.
  const text = (cell.text || "").toString();
  if (text) return text;
  const v = cell.value;
  if (v == null) return "";
  if (typeof v === "object") {
    if (v instanceof Date) return v.toISOString();
    const anyV = v as unknown as { result?: unknown; richText?: Array<{ text: string }>; hyperlink?: string };
    if (anyV.result != null) return String(anyV.result);
    if (anyV.richText) return anyV.richText.map((t: { text: string }) => t.text).join("");
  }
  return String(v);
}

function cellDecimal(cell: ExcelJS.Cell): Decimal {
  const v = cell.value;
  if (v == null) return new Decimal(0);
  if (typeof v === "number") return new Decimal(v);
  if (typeof v === "string") return safeParseDecimal(v);
  if (typeof v === "object") {
    const anyV = v as unknown as { result?: unknown };
    if (anyV.result != null) {
      if (typeof anyV.result === "number") return new Decimal(anyV.result);
      if (typeof anyV.result === "string") return safeParseDecimal(anyV.result);
    }
  }
  return safeParseDecimal(cellText(cell));
}

// Pass-through primitive read for source rows we're piping into
// ExcelComposer. Preserves number / boolean / Date typing where the
// source cell has it; falls back to displayed text otherwise.
function cellPrimitive(cell: ExcelJS.Cell): string | number | boolean | Date {
  const v = cell.value;
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  if (v instanceof Date) return v;
  const anyV = v as unknown as { result?: unknown; richText?: Array<{ text: string }> };
  if (anyV.result != null) {
    const r = anyV.result;
    if (typeof r === "string" || typeof r === "number" || typeof r === "boolean") return r;
    if (r instanceof Date) return r;
    return String(r);
  }
  if (anyV.richText) return anyV.richText.map((t: { text: string }) => t.text).join("");
  return cell.text || "";
}

// -----------------------------------------------------------------------
// Acres entry — one per contributing profile, or a zero placeholder when
// a customer + year + bucket combination has no matching profile.
// -----------------------------------------------------------------------
interface AcresEntry {
  customerLongName: string;
  cropYear: number;
  commodityName: string;
  acreage: Decimal;
  isMissing: boolean;
}

function displayCommodityForBucket(bucket: CommodityBucket): string {
  switch (bucket) {
    case "wheat":
      return "Wheat";
    case "corn":
      return "Corn";
    case "soybeans":
      return "Soybeans";
    case "canola":
      return "Canola";
  }
}

// -----------------------------------------------------------------------
// Main entrypoint. Takes the Producer Risk source tab + the crop-profile
// dataset (already fetched by the caller), classifies rows, and builds
// a fresh workbook (NC, OC, NC summary, OC summary, Acres) via the
// shared ExcelComposer. Source styling is NOT preserved on NC/OC by
// product decision — these tabs are pure data dumps.
// -----------------------------------------------------------------------
export async function buildOcNcWorkbook(params: {
  producerRiskSheet: ExcelJS.Worksheet;
  profiles: ReportCropProfileRow[];
  ncYear: number;
  ocYear: number;
}): Promise<ProcessResult> {
  const { producerRiskSheet, profiles, ncYear, ocYear } = params;

  const profileIndex = indexProfiles(profiles);

  const ncRows: KeptRow[] = [];
  const ocRows: KeptRow[] = [];

  const errors: ProcessingErrors = {
    unknownCommodities: [],
    invalidDates: [],
    unknownCustomers: [],
  };
  let totalDataRows = 0;

  // Single pass over the source rows.
  producerRiskSheet.eachRow((row: ExcelJS.Row, rowNumber: number) => {
    if (rowNumber < DATA_START_ROW) return;

    const commodityCell = row.getCell(SOURCE_COMMODITY_COL);
    const customerCell = row.getCell(SOURCE_CUSTOMER_COL);
    const dateCell = row.getCell(SOURCE_DELIVERY_END_DATE_COL);
    const qtyCell = row.getCell(SOURCE_OPEN_QTY_COL);
    const gainLossCell = row.getCell(SOURCE_GAIN_LOSS_COL);

    const commodityText = cellText(commodityCell);
    const customerText = cellText(customerCell).trim();

    // Treat fully blank rows as not data — some Producer Risk exports
    // have trailing empty rows ExcelJS still iterates.
    if (!commodityText && !customerText && !cellText(dateCell)) return;

    totalDataRows++;

    const bucket = classifyCommodity(commodityText);
    if (!bucket) {
      errors.unknownCommodities.push({
        row: rowNumber,
        commodity: commodityText,
        customer: customerText,
      });
      return;
    }

    const parsedDate = parseDeliveryEndDate(dateCell.value, cellText(dateCell));
    const classification = classifyOcNc({ bucket, endDate: parsedDate, ncYear });
    if (classification === "invalid-date") {
      errors.invalidDates.push({
        row: rowNumber,
        commodity: commodityText,
        customer: customerText,
        raw: parsedDate.kind === "invalid" ? parsedDate.raw : "",
      });
      return;
    }

    const kept: KeptRow = {
      sourceRowNumber: rowNumber,
      customerLongName: customerText,
      bucket,
      openQty: cellDecimal(qtyCell),
      gainLossTotal: cellDecimal(gainLossCell),
    };

    if (classification === "OC") {
      ocRows.push(kept);
    } else {
      ncRows.push(kept);
    }
  });

  // Track unknown customers (present in NC/OC rows but no profile rows
  // for any commodity / year). Surfaced to the user; rows are NOT
  // dropped — they just get a zero acres value in the Acres tab.
  const allKeptCustomers = new Set<string>();
  ncRows.forEach((r: KeptRow) => allKeptCustomers.add(r.customerLongName));
  ocRows.forEach((r: KeptRow) => allKeptCustomers.add(r.customerLongName));
  for (const customer of allKeptCustomers) {
    if (!profileIndex.byCustomerYearType.has(normalizeCustomer(customer))) {
      errors.unknownCustomers.push(customer);
    }
  }
  errors.unknownCustomers.sort();

  // -------------------------------------------------------------------
  // Build the output workbook via ExcelComposer.
  // -------------------------------------------------------------------
  const lastCol = producerRiskSheet.columnCount;
  const headerRowRef = producerRiskSheet.getRow(HEADER_ROW);
  const headerStrings: string[] = [];
  for (let c = 1; c <= lastCol; c++) {
    headerStrings.push(cellText(headerRowRef.getCell(c)));
  }

  const readRowAsCellValues = (rowNumber: number): ComposerCellValue[] => {
    const sourceRow = producerRiskSheet.getRow(rowNumber);
    const out: ComposerCellValue[] = [];
    for (let c = 1; c <= lastCol; c++) {
      out.push({ value: cellPrimitive(sourceRow.getCell(c)) });
    }
    return out;
  };

  const composer = new ExcelComposer();

  const detailSectionConfig = (rows: KeptRow[]): SectionConfig => ({
    headers: headerStrings,
    data: rows.map((r: KeptRow) => readRowAsCellValues(r.sourceRowNumber)),
  });

  composer.createSheet("NC");
  composer.addSection(detailSectionConfig(ncRows), { startRow: 1, startColumn: 1 });

  composer.createSheet("OC");
  composer.addSection(detailSectionConfig(ocRows), { startRow: 1, startColumn: 1 });

  composer.createSheet("NC summary");
  const ncSummaryAcres = addSummarySection({ composer, rows: ncRows, cropYear: ncYear, profileIndex });

  composer.createSheet("OC summary");
  const ocSummaryAcres = addSummarySection({ composer, rows: ocRows, cropYear: ocYear, profileIndex });

  composer.createSheet("Acres");
  addAcresSection({ composer, entries: [...ncSummaryAcres, ...ocSummaryAcres] });

  const buffer = await composer.getBuffer();
  return {
    buffer,
    errors,
    counts: {
      totalDataRows,
      ncRows: ncRows.length,
      ocRows: ocRows.length,
      skippedUnknownCommodity: errors.unknownCommodities.length,
      skippedInvalidDate: errors.invalidDates.length,
    },
    ncYear,
    ocYear,
  };
}

// -----------------------------------------------------------------------
// Summary section — groups kept rows by customer, sums Quantity +
// Gain/Loss, looks up acres from the crop-profile index, and emits a
// `C$ROW/D$ROW` formula for per-acre. Returns the acreage entries it
// touched so the Acres tab can render the contributing-profile detail.
// -----------------------------------------------------------------------
function addSummarySection(params: {
  composer: ExcelComposer;
  rows: KeptRow[];
  cropYear: number;
  profileIndex: ProfileIndex;
}): AcresEntry[] {
  const { composer, rows, cropYear, profileIndex } = params;

  const byCustomer = new Map<string, KeptRow[]>();
  for (const row of rows) {
    const list = byCustomer.get(row.customerLongName) ?? [];
    list.push(row);
    byCustomer.set(row.customerLongName, list);
  }

  const customerList = Array.from(byCustomer.keys());

  const acresEntries: AcresEntry[] = [];

  interface CustomerAggregate {
    customer: string;
    quantity: Decimal;
    gainLoss: Decimal;
    acres: Decimal;
    gainLossPerAcre: Decimal | null;
  }

  const aggregates: CustomerAggregate[] = [];

  for (const customer of customerList) {
    const customerRows = byCustomer.get(customer)!;
    const quantity = customerRows.reduce((acc: Decimal, r: KeptRow) => acc.plus(r.openQty), new Decimal(0));
    const gainLoss = customerRows.reduce((acc: Decimal, r: KeptRow) => acc.plus(r.gainLossTotal), new Decimal(0));

    // Pull every profile this customer has for the crop year, not just
    // the ones whose commodity_type matched a row in the source data.
    const allProfiles = lookupAllProfilesForCustomerYear({
      index: profileIndex,
      customerLongName: customer,
      cropYear,
    });

    let acres = new Decimal(0);
    if (allProfiles.length === 0) {
      // Record one placeholder per bucket the customer actually traded
      // in so the Acres tab still flags the missing-profile situation.
      const buckets = new Set<CommodityBucket>(customerRows.map((r: KeptRow) => r.bucket));
      for (const bucket of buckets) {
        acresEntries.push({
          customerLongName: customer,
          cropYear,
          commodityName: displayCommodityForBucket(bucket),
          acreage: new Decimal(0),
          isMissing: true,
        });
      }
    } else {
      for (const profile of allProfiles) {
        const profileAcres = profile.acreage ? safeParseDecimal(profile.acreage) : new Decimal(0);
        acres = acres.plus(profileAcres);
        acresEntries.push({
          customerLongName: customer,
          cropYear,
          commodityName: getDisplayCommodityForType(profile.commodity_type),
          acreage: profileAcres,
          isMissing: false,
        });
      }
    }

    const gainLossPerAcre = acres.isZero() ? null : gainLoss.div(acres);

    aggregates.push({
      customer,
      quantity,
      gainLoss,
      acres,
      gainLossPerAcre,
    });
  }

  // Sort: no-acreage rows first (by customer name), then rows with
  // acreage by Gain/Loss Per Acre DESC, ties broken by customer name.
  aggregates.sort((a: CustomerAggregate, b: CustomerAggregate) => {
    const aNoAcres = a.gainLossPerAcre === null;
    const bNoAcres = b.gainLossPerAcre === null;
    if (aNoAcres && !bNoAcres) return -1;
    if (!aNoAcres && bNoAcres) return 1;
    if (aNoAcres && bNoAcres) return a.customer.localeCompare(b.customer);
    const cmp = b.gainLossPerAcre!.comparedTo(a.gainLossPerAcre!);
    if (cmp !== 0) return cmp;
    return a.customer.localeCompare(b.customer);
  });

  const data: ComposerCellValue[][] = aggregates.map((agg: CustomerAggregate) => {
    const hasAcres = agg.gainLossPerAcre !== null;
    // Highlight Gain/Loss Per Acre when the customer has acreage AND
    // the per-acre value is > 20.
    const highlightPerAcre = hasAcres && agg.gainLossPerAcre!.greaterThan(20);
    // Highlight Gain/Loss Total when the customer has NO acreage AND
    // the total is > 100,000.
    const highlightGainLoss = !hasAcres && agg.gainLoss.greaterThan(100000);

    const gainLossCell: ComposerCellValue = { value: agg.gainLoss.toNumber(), format: CellFormat.NUMBER };
    if (highlightGainLoss) gainLossCell.fill = Fills.lightYellow;

    // ExcelComposer expands `$ROW` to the actual row number at write
    // time, so we write the formula once with placeholders.
    const perAcreCell: ComposerCellValue = { formula: "=C$ROW/D$ROW", format: CellFormat.ACCOUNTING };
    if (highlightPerAcre) perAcreCell.fill = Fills.lightYellow;

    return [
      { value: agg.customer },
      { value: agg.quantity.toNumber(), format: CellFormat.NUMBER },
      gainLossCell,
      { value: agg.acres.toNumber(), format: CellFormat.NUMBER },
      perAcreCell,
    ];
  });

  composer.addSection({
    headers: ["Client", "Quantity", "Gain/Loss Total", "Acres", "Gain/Loss Per Acre"],
    data,
    styling: {
      header: { bold: true },
      columns: {
        A: { width: 36 },
        B: { width: 16 },
        C: { width: 18 },
        D: { width: 14 },
        E: { width: 20 },
      },
    },
  }, { startRow: 1, startColumn: 1 });

  return acresEntries;
}

// -----------------------------------------------------------------------
// Acres section — one row per contributing profile (or zero-acre
// placeholder for customer/year/commodity combos with no profile).
// Sorted by Client, then Crop Year, then Commodity.
// -----------------------------------------------------------------------
function addAcresSection(params: {
  composer: ExcelComposer;
  entries: AcresEntry[];
}): void {
  const { composer, entries } = params;
  const sorted = [...entries].sort((a: AcresEntry, b: AcresEntry) => {
    const c = a.customerLongName.localeCompare(b.customerLongName);
    if (c !== 0) return c;
    if (a.cropYear !== b.cropYear) return a.cropYear - b.cropYear;
    return a.commodityName.localeCompare(b.commodityName);
  });

  const data: ComposerCellValue[][] = sorted.map((entry: AcresEntry) => [
    { value: entry.customerLongName },
    { value: entry.cropYear },
    { value: entry.commodityName },
    { value: entry.acreage.toNumber(), format: CellFormat.NUMBER },
  ]);

  composer.addSection({
    headers: ["Client", "Crop Year", "Commodity", "Acreage"],
    data,
    styling: {
      header: { bold: true },
      columns: {
        A: { width: 36 },
        B: { width: 12 },
        C: { width: 24 },
        D: { width: 14 },
      },
    },
  }, { startRow: 1, startColumn: 1 });
}

function getDisplayCommodityForType(commodity_type: string) {
  switch(commodity_type) {
    case Commodities.Corn:
      return "Corn";
    case Commodities.Soybeans:
      return "Soybeans";
    case Commodities.Canola:
      return "Canola";
    case Commodities.CanadianDollar:
      return "CAD/USD";
    case Commodities.MinneapolisWheat:
    case Commodities.WinterWheat:
    case Commodities.WinterWheatPrelimCommoditySymbol:
      return "Wheat";
  }
  return "";
}
